import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { UserDetailsPartial } from 'src/auth/types';

type User = {
    loggedIn: boolean;
    details?: UserDetailsPartial;
    tokenDetails?: Record<number, string>;
    loginError?: string;
    tokenExpired?: boolean;
    isOccupantFlow: boolean;
    hasBookings: boolean;
};

const initialState: User = {
    loggedIn: false,
    tokenExpired: false,
    isOccupantFlow: false,
    hasBookings: false,
};

const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        setLoggedIn: (state, action: PayloadAction<boolean>) => {
            state.loggedIn = action.payload;
        },
        setDetails: (state, action: PayloadAction<UserDetailsPartial>) => {
            state.details = action.payload;
        },
        clearDetails: (state) => {
            delete state.details;
        },
        setTokenDetails: (state, action) => {
            state.tokenDetails = action.payload;
        },
        setTokenExpired: (state, action) => {
            state.tokenExpired = action.payload;
        },
        setIsOccupantFlow: (state, action: PayloadAction<boolean>) => {
            state.isOccupantFlow = action.payload;
        },
        setUserHasBookings: (state, action: PayloadAction<boolean>) => {
            state.hasBookings = action.payload;
        },
    },
});

export const {
    setLoggedIn,
    setDetails,
    clearDetails,
    setTokenDetails,
    setTokenExpired,
    setIsOccupantFlow,
    setUserHasBookings,
} = userSlice.actions;

export default userSlice.reducer;

const selectUserState = (state: { user: User }) => state.user;

export const selectUserLoggedIn = createSelector(
    [selectUserState],
    (userState) => userState.loggedIn
);

export const selectUserDetails = createSelector(
    [selectUserState],
    (userState) => userState.details
);

export const selectUserName = createSelector([selectUserDetails], (details) =>
    details?.preferredName ? details?.preferredName : details?.firstName
);

export const selectTokenExpired = createSelector(
    [selectUserState],
    (userState) => userState.tokenExpired
);

export const selectUserIsOccupantFlow = createSelector(
    [selectUserState],
    (userState) => userState.isOccupantFlow
);

export const selectLoginError = createSelector(
    [selectUserState],
    (userState) => userState.loginError
);

export const selectTokenDetails = createSelector(
    [selectUserState],
    (userState) => userState.tokenDetails
);

export const selectUserHasBookings = createSelector(
    [selectUserState],
    (userState) => userState.hasBookings
);

export const selectUserHasSpanishResidenceInfo = createSelector(
    [selectUserDetails],
    (userDetails) => {
        if (!userDetails) {
            return false;
        }

        return Boolean(
            userDetails.nieNumber || userDetails.nifNumber || userDetails.passportNumber
        );
    }
);
